import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Button, Dropdown, Modal, Skeleton } from 'antd'
import { useTranslation } from 'react-i18next'
import { getUMKFromLocal } from 'pUtils'
import { listCredentials, isUmkFresh } from 'services/passkey'
import { maybeRegisterCurrentDevice } from 'services/passkey/deviceUnlock'
import { errorBase } from 'utils/ui'

import PasskeyRegisterModal from 'pages/auth/components/PasskeyRegisterModal'
import PasskeyDeleteConfirmModal from './PasskeyDeleteConfirmModal'
import { formatLastUsed } from './passkeyHelpers'
import { getPasskeyProviderIcon } from './passkeyProviderIcons'
import { showStaleSessionPopup } from './staleSessionPopup'
import styles from '../style.module.scss'

import moreIcon from '@/assets/images/icon-more-horizontal-24.svg'
import manageIcon from '@/assets/images/passkey/icon-passkey-manage-22.svg'

// default 콜백 prop 은 렌더마다 새 함수 인스턴스가 생기지 않도록 모듈 레벨 고정 참조 사용
const NOOP = () => {}

const ManagePasskeysModal = ({
  open = false,
  onClose = NOOP,
  onChanged = NOOP,
  onLastDeviceDeleted = NOOP,
}) => {
  const { t, i18n } = useTranslation()
  const [credentials, setCredentials] = useState([])
  const [loading, setLoading] = useState(false)
  const [registerOpen, setRegisterOpen] = useState(false)
  const [umk, setUmk] = useState(null)
  const [renameTarget, setRenameTarget] = useState(null) // { id, label }
  const [deleteTarget, setDeleteTarget] = useState(null) // { id, label }

  // onChanged 를 deps 에 포함시키면 호출자가 인라인 함수를 넘길 때
  // refresh identity 가 매 렌더 바뀌어 아래 useEffect 가 무한 루프에 빠진다.
  // 부수효과 통지용이므로 ref 로 최신값만 들고 있는다.
  const onChangedRef = useRef(onChanged)
  useEffect(() => {
    onChangedRef.current = onChanged
  })

  const refresh = useCallback(async () => {
    setLoading(true)
    let list = null
    try {
      list = await listCredentials()
      setCredentials(list)
    } catch {
      // gemini 리뷰 #1323 R1: 조회 실패를 [] 로 반환하면 onDeleted 의 length===0 검사가
      // "마지막 패스키 삭제됨"으로 오판 → 디바이스 강제 revoke 오발동. null 로 실패를 구분하고
      // 기존 목록은 비우지 않는다 (일시 네트워크 오류에 목록이 사라지는 현상 방지).
      return null
    } finally {
      setLoading(false)
    }
    // 통지를 조회 try 밖으로 분리 (#1319 ⑥): onChanged(소비자) throw 가 '조회 실패' catch 로
    // 빠지면 setCredentials([]) + return [] 로 "패스키 0개" 오판 → 마지막-삭제 처리 오발동 위험.
    try {
      onChangedRef.current?.(list)
    } catch (notifyErr) {
      console.warn('[passkey] onChanged consumer failed:', notifyErr)
    }
    return list
  }, [])

  useEffect(() => {
    if (open) refresh()
  }, [open, refresh])

  const handleOpenRegister = async () => {
    const current = await getUMKFromLocal(true)
    if (!current) {
      // UMK 만료/잠금 — 조용한 무반응 대신 안내 (#1319 ⑧, PasskeyManagement.handleEnable 과 동일 정책)
      errorBase({ title: t('appConfig.settings.passkeyUmkExpired') })
      return
    }
    // #1509 후속: stale UMK(다른 브라우저에서 비번 변경) → credential 만 등록되는 반쪽 실패를
    // ceremony 전에 차단 (PasskeyManagement.handleEnable 과 동일 정책)
    if (!(await isUmkFresh(current))) {
      showStaleSessionPopup(t)
      return
    }
    setUmk(current)
    setRegisterOpen(true)
  }

  const buildMenu = (cred) => ({
    items: [
      {
        key: 'rename',
        label: t('appConfig.settings.passkeyRenameMenu'),
        onClick: () => setRenameTarget({ id: cred.id, label: cred.device_label }),
      },
      {
        key: 'delete',
        label: t('appConfig.settings.passkeyDelete'),
        className: styles.deleteMenuItem,
        onClick: () => setDeleteTarget({ id: cred.id, label: cred.device_label }),
      },
    ],
  })

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        destroyOnClose
        width={480}
        className={styles.managePasskeysModal}
        closable={false}
        footer={
          <Button className={styles.cancelBtn} onClick={onClose}>
            {t('common.button.cancel', 'Cancel')}
          </Button>
        }
      >
        <div className={styles.managePasskeysContent}>
          <img src={manageIcon} alt="" className={styles.headerIcon} />
          <div className={styles.managePasskeysRight}>
            <p className={styles.managePasskeysTitle}>
              {t('appConfig.settings.passkeyManageTitle')}
            </p>
            <p className={styles.managePasskeysDesc}>{t('appConfig.settings.passkeyManageDesc')}</p>

            {loading ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : (
              <div className={styles.passkeyList}>
                {credentials.map((cred) => {
                  const name = cred.device_label || t('appConfig.settings.passkeyUnnamedDevice')
                  const lastUsed = `${t('appConfig.settings.passkeyLastUsed')}: ${formatLastUsed(t, cred.last_used_at, i18n.language)}`
                  return (
                    <div key={cred.id} className={styles.passkeyListItem}>
                      <div className={styles.passkeyIconBox}>
                        <img src={getPasskeyProviderIcon(cred.aaguid)} alt="" />
                      </div>
                      <div className={styles.passkeyInfo}>
                        <div className={styles.passkeyName}>{name}</div>
                        <div className={styles.passkeyTime}>{lastUsed}</div>
                      </div>
                      <Dropdown
                        menu={buildMenu(cred)}
                        trigger={['click']}
                        overlayClassName={classNames(styles.passkeyMenu)}
                        placement="bottomRight"
                      >
                        <button type="button" aria-label="more" className={styles.moreBtn}>
                          <img src={moreIcon} alt="" />
                        </button>
                      </Dropdown>
                    </div>
                  )
                })}
              </div>
            )}

            <div className={styles.addPasskeyWrap}>
              <Button className={classNames('type8', 'h38')} onClick={handleOpenRegister}>
                {t('appConfig.settings.passkeyAddNew')}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <PasskeyRegisterModal
        open={registerOpen}
        umk={umk}
        onClose={() => {
          setRegisterOpen(false)
          setUmk(null)
        }}
        onRegistered={async (result) => {
          // PR-2 (#1301, 설계 §5.2-(3)): 새 패스키 등록 시 이 브라우저 디바이스도 자동 등록.
          // setUmk(null) 이 먼저 실행되므로 선캡처 (PasskeyManagement C1 동일 패턴).
          const currentUmk = umk
          setRegisterOpen(false)
          setUmk(null)
          await refresh()
          // #1319 ③: 자동 등록 실패가 조용히 사라지면 사용자가 미설정 상태를 모름 → 안내
          const deviceReady = await maybeRegisterCurrentDevice(currentUmk, result)
          if (!deviceReady) {
            errorBase({ title: t('appConfig.settings.passkeyDeviceCreateFailed') })
          }
        }}
      />
      <PasskeyRegisterModal
        open={!!renameTarget}
        mode="rename"
        credentialId={renameTarget?.id}
        initialLabel={renameTarget?.label}
        onClose={() => setRenameTarget(null)}
        onRenamed={async () => {
          setRenameTarget(null)
          await refresh()
        }}
      />
      <PasskeyDeleteConfirmModal
        open={!!deleteTarget}
        credentialId={deleteTarget?.id}
        deviceLabel={deleteTarget?.label}
        onClose={() => setDeleteTarget(null)}
        onDeleted={async () => {
          setDeleteTarget(null)
          const next = await refresh()
          // gemini 리뷰 #1323 R2: null = 조회 실패 — "0개"와 구분해 마지막-삭제 처리 오발동 차단
          if (next !== null && next.length === 0) {
            // #1319 ④: 소비자(handleLastDeviceDeleted)의 revokeCurrentDevice 는 로컬 정리 실패
            // 시 throw 전파 — await 로 잡아 DeleteConfirmModal 의 post-delete catch 가 흡수하게
            // 하되, 예외가 나도 이 모달은 닫히도록 finally 로 보장 (빈 목록 모달 잔존 방지).
            try {
              await onLastDeviceDeleted?.()
            } finally {
              onClose?.()
            }
          }
        }}
      />
    </>
  )
}

ManagePasskeysModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onChanged: PropTypes.func,
  onLastDeviceDeleted: PropTypes.func,
}

export default ManagePasskeysModal
